import { OktaAuth } from '@okta/okta-auth-js';

const AUTH_SCOPES = [
  'openid',
  'email',
  'profile',
  'console',
  'connectedsearch.query',
  'connectedservice.query',
  'machinelearning.model',
];

// Module-level flag shared across ALL OktaAuthService instances. The app creates
// many CCApiService instances (one per domain service), each with its own
// OktaAuthService, but there is exactly one user and one auth flow per page load.
// Making this module-level means every instance reads and writes the same value,
// so a reauth initiated by the capabilities service correctly suppresses a
// concurrent 401 from the customers service — eliminating the cross-instance flash.
// After signInWithRedirect() the browser does a full-page navigation to Okta and
// back, which tears down and recreates the JS module context, so the flag naturally
// resets to false on return — post-return 401s are not suppressed and can reach
// the loop-breaker as expected.
let authInFlight = false;

// Exported for test isolation only — resets the shared flag between test cases.
export const resetAuthInFlightForTesting = () => {
  authInFlight = false;
};

export class OktaAuthService {
  sdk: OktaAuth;

  // True while a signInWithRedirect / reauthenticate is in progress.
  // Cleared by handleLoginRedirect() when the OAuth callback completes.
  // Backed by the module-level authInFlight variable so all instances share state.
  get isAuthInFlight(): boolean {
    return authInFlight;
  }
  set isAuthInFlight(value: boolean) {
    authInFlight = value;
  }

  constructor(clientId: string, issuer: string) {
    this.sdk = new OktaAuth({
      clientId,
      issuer,
      redirectUri: `${globalThis.location.origin}/login/oauth2`,
      postLogoutRedirectUri: `${globalThis.location.origin}/logout`,
    });
  }

  // Capture the full location (path + query string + hash) so the post-login
  // redirect returns the user to exactly where they were. Query params carry
  // real state here (capability filters, sub-tabs), so pathname alone is not
  // enough. Never restore the /error page — fall back to root instead.
  private readonly getOriginalUri = () => {
    if (globalThis.location.pathname.startsWith('/error')) {
      return '/';
    }
    return globalThis.location.pathname + globalThis.location.search + globalThis.location.hash;
  };

  authGuard = async (): Promise<void> => {
    const authenticated = await this.sdk.isAuthenticated({ onExpiredToken: 'none' });
    if (authenticated) {
      return;
    }

    this.isAuthInFlight = true;
    this.sdk.setOriginalUri(this.getOriginalUri());
    // signInWithRedirect triggers a full-page navigation to Okta. The returned
    // promise resolves immediately (before the browser navigates away), so we
    // must NOT resolve our own promise at that point — doing so would allow the
    // Vaadin router to render the destination component while the redirect is
    // still in flight, causing protected API calls to fire with a missing token.
    // Instead we return a promise that intentionally never resolves (the browser
    // navigates away anyway). On failure we clear the flag and re-throw so
    // callers get a real error rather than hanging forever.
    return new Promise<void>((_resolve, reject) => {
      this.sdk.signInWithRedirect({ scopes: AUTH_SCOPES }).catch((err: unknown) => {
        this.isAuthInFlight = false;
        reject(err);
      });
      // Intentionally no resolve() call — the browser navigates away.
    });
  };

  // Force a re-authentication regardless of the local token state. Used when the
  // server rejects our token with a 401 even though it may still look valid
  // client-side (revoked grant, audience/scope mismatch, clock skew). authGuard
  // would no-op in that case because isAuthenticated() returns true locally.
  reauthenticate = async () => {
    this.isAuthInFlight = true;
    this.sdk.setOriginalUri(this.getOriginalUri());
    return this.sdk.signInWithRedirect({ scopes: AUTH_SCOPES }).catch((err: unknown) => {
      this.isAuthInFlight = false;
      throw err;
    });
  };

  // Called by the /login/oauth2 route action when the Okta OAuth callback lands.
  // Delegates to the SDK, then clears the in-flight flag. The loop-breaker attempt counter
  // set in handle401() (in api-service) is intentionally NOT cleared here: if a 401
  // arrives immediately after returning from Okta — meaning the newly-issued token is
  // still rejected — it must hit the loop-breaker in handle401() and show the error
  // page rather than bouncing through Okta again. The attempt counter is cleared on the first
  // successful 2xx API response in performFetch, which is the correct moment to
  // consider auth fully resolved.
  handleLoginRedirect = async (): Promise<void> => {
    await this.sdk.handleLoginRedirect();
    this.isAuthInFlight = false;
  };

  getTokens = async (forceAuth = false) => {
    await this._forceAuth(forceAuth);
    return this.sdk.tokenManager.getTokens();
  };

  getUserInfo = async (forceAuth = false) => {
    await this._forceAuth(forceAuth);
    return this.sdk.token.getUserInfo();
  };

  logout = async () => {
    return this.sdk.signOut();
  };

  _forceAuth = async (forceAuth: boolean) => {
    if (forceAuth) {
      await this.authGuard();
    }
  };
}
